import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface Kpi {
  readonly kpiId: string;
  readonly kpiCode: string;
  readonly kpiName: string;
  readonly kpiCategory: string;
  /** The owner, named by the server (G18) — never the uuid the row stores. */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly formulaVersion: number;
}

// Mirrors backend KPI_CATS (kpi.service) — server rejects any other value.
const KPI_CATEGORIES = ['OPERATIONAL', 'FINANCIAL', 'COMPLIANCE', 'AUDIT', 'VENDOR', 'CAMPAIGN', 'EXECUTION', 'PAYMENT', 'MANAGEMENT'];

/** M26 — KPI Repository (§11/§12, BR-02/BR-03). Owner + version per KPI;
 *  create (POST /v1/dwh/kpis) and version-controlled formula edit
 *  (PATCH :id/formula) surfaced over the guarded KPI_MANAGE endpoints. */
export function M26KpiRepositoryPage(): ReactNode {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [rows, setRows] = useState<Kpi[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [kpiName, setKpiName] = useState('');
  const [kpiCategory, setKpiCategory] = useState('');
  const [formulaExpression, setFormulaExpression] = useState('');

  const load = useCallback((): void => {
    setLoading(true);
    apiRequest<{ data: Kpi[]; total: number }>('/v1/dwh/kpis', { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function onCreate(e: FormEvent): void {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('/v1/dwh/kpis', {
      method: 'POST',
      token,
      body: { kpiName, kpiCategory: kpiCategory || undefined, formulaExpression, ownerId: user?.id },
    })
      .then(() => { setNotice(t('m26.kpi.kpiCreated')); setKpiName(''); setKpiCategory(''); setFormulaExpression(''); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  function editFormula(kpiId: string): void {
    const formula = window.prompt(t('m26.kpi.promptFormula'));
    if (formula === null || formula.trim().length === 0) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest(`/v1/dwh/kpis/${encodeURIComponent(kpiId)}/formula`, {
      method: 'PATCH',
      token,
      body: { formula: formula.trim() },
    })
      .then(() => { setNotice(t('m26.kpi.formulaUpdated')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="kpi-repository">
      <h1>{t('m26.kpi.title')}</h1>
      <form aria-label="kpi-create-form" onSubmit={onCreate}>
        <label htmlFor="kpi-name">{t('m26.kpi.kpiNameLabel')}</label>
        <input id="kpi-name" value={kpiName} onChange={(e) => setKpiName(e.target.value)} required />
        <label htmlFor="kpi-category">{t('m26.kpi.categoryLabel')}</label>
        <select id="kpi-category" value={kpiCategory} onChange={(e) => setKpiCategory(e.target.value)}>
          <option value="">{t('m26.kpi.defaultOperational')}</option>
          {KPI_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label htmlFor="kpi-formula">{t('m26.kpi.formulaLabel')}</label>
        <input id="kpi-formula" value={formulaExpression} onChange={(e) => setFormulaExpression(e.target.value)} required />
        <button type="submit" disabled={busy}>{t('m26.kpi.createKpi')}</button>
      </form>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.kpi.loading')}</p> : (
        <>
          <p aria-label="kpi-count">{t('m26.kpi.count', { count: total })}</p>
          <table aria-label="kpi-table">
            <thead><tr><th>{t('m26.kpi.colCode')}</th><th>{t('m26.kpi.colName')}</th><th>{t('m26.kpi.colCategory')}</th><th>{t('m26.kpi.colOwner')}</th><th>{t('m26.kpi.colVersion')}</th><th>{t('m26.kpi.colActions')}</th></tr></thead>
            <tbody>{rows.map((k) => (
              <tr key={k.kpiId}>
                <td>{k.kpiCode}</td><td>{k.kpiName}</td><td>{k.kpiCategory}</td><td>{personLabel(k.actorName, k.actorCode) ?? '—'}</td><td>v{k.formulaVersion}</td>
                <td><button type="button" aria-label={`edit-formula-${k.kpiId}`} disabled={busy} onClick={() => editFormula(k.kpiId)}>{t('m26.kpi.editFormula')}</button></td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
